import { auth } from '../firebase';
import { env } from '../config/environment';
import { acceptedWarningTexts, confirmDocumentWarnings } from './documentWarningPrompt';

/**
 * Create a new client document folder (always a new Firestore doc).
 * @returns {Promise<{ id: string, name: string }>}
 */
export async function createClientDocumentFolder(clientId, name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) throw new Error('Folder name is required.');
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in.');

  const url = env.isDevelopment
    ? `/api/clients/${clientId}/document-folders`
    : env.apiEndpoints.clientDocumentFolders(clientId);

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name: trimmed, parentId: null }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Failed to create folder "${trimmed}"`);
  }
  if (!data.id) throw new Error('Folder was created but no id was returned.');
  return { id: data.id, name: data.name || trimmed };
}

/**
 * Make (or adopt) the client's standard folders — Onboarding SOA, Projection
 * Comparison, Compliance Documents (src/utils/standardFolders.js) — and return
 * every folder of the client.
 * @returns {Promise<Array<{ id: string, name: string, parentId: string|null, standardFolder?: string }>>}
 */
export async function ensureClientStandardFolders(clientId) {
  if (!clientId) throw new Error('Missing client.');
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in.');
  const res = await fetch(env.apiEndpoints.clientStandardFolders(clientId), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !Array.isArray(data.folders)) {
    throw new Error(data.error || `Failed to load the client's folders (${res.status})`);
  }
  return data.folders;
}

/** Best-effort form for client-creation paths: never throws. */
export async function ensureClientStandardFoldersQuietly(clientId) {
  try {
    return await ensureClientStandardFolders(clientId);
  } catch (err) {
    console.warn('Standard folders not made:', err?.message || err);
    return null;
  }
}

/**
 * Generate native Word documents into client document storage.
 *
 * @param {object} params
 * @param {string} params.clientId
 * @param {string[]} params.documentTypes
 * @param {'existing'|'new'} [params.folderDestination]
 * @param {string|null} [params.folderId] — when folderDestination is 'existing'
 * @param {string|null} [params.folderName] — when folderDestination is 'new'
 * @param {boolean} [params.createPackFolder] — legacy fallback
 * @param {Record<string, 'docx'|'pdf'>} [params.formats] — per document (the compliance file notes can be PDFs)
 * @returns {Promise<{ generated: object[], errors?: object[], folderId?: string, folderName?: string }>}
 */
export async function generateClientDocuments({
  clientId,
  documentTypes,
  folderDestination = 'new',
  folderId = null,
  folderName = null,
  createPackFolder = false,
  formats = {},
}) {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in.');

  let targetFolderId = folderId;
  let targetFolderName = null;

  if (folderDestination === 'new') {
    if (!folderName || !String(folderName).trim()) {
      throw new Error('Folder name is required for new folder.');
    }
    const created = await createClientDocumentFolder(clientId, folderName);
    targetFolderId = created.id;
    targetFolderName = created.name;
  } else if (folderDestination === 'existing') {
    if (!targetFolderId) throw new Error('Select a folder to save documents into.');
  }

  const url = env.isDevelopment
    ? `/api/clients/${clientId}/documents/generate`
    : env.apiEndpoints.clientDocumentsGenerate(clientId);

  const post = async (types, extra = {}) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        documentTypes: types,
        folderDestination: 'existing',
        folderId: targetFolderId,
        createPackFolder: false,
        formats,
        ...extra,
      }),
    });
    return { res: response, data: await response.json().catch(() => ({})) };
  };

  let { res, data } = await post(documentTypes);

  // Documents with a warning weren't made: on Proceed anyway, make those and add them to the rest.
  const problems = (res.ok ? data.generationErrors : data.details) || [];
  const warned = Array.isArray(problems) ? problems.filter((e) => e.canProceed) : [];
  const warnings = warned.flatMap((e) => e.warnings || []);
  if (warned.length > 0 && await confirmDocumentWarnings(warnings)) {
    const others = problems.filter((e) => !e.canProceed);
    const retry = await post(warned.map((e) => e.documentType), { proceedAnyway: true, acceptedWarnings: acceptedWarningTexts(warnings) });
    const generated = [...(data.generated || []), ...(retry.data.generated || [])];
    const generationErrors = [...others, ...(retry.res.ok ? retry.data.generationErrors || [] : retry.data.details || [{ error: retry.data.error }])];
    if (generated.length > 0) {
      res = { ok: true };
      data = { ...data, ...retry.data, generated, generationErrors: generationErrors.length > 0 ? generationErrors : undefined };
    } else {
      res = { ok: false, status: retry.res.status };
      data = { error: retry.data.error, details: generationErrors };
    }
  }

  if (!res.ok) {
    if (Array.isArray(data.details) && data.details.length > 0) {
      const detailText = data.details
        .map((d) => (d.documentType ? `${d.documentType}: ${d.error}` : d.error))
        .filter(Boolean)
        .join(' · ');
      throw new Error(detailText || data.error || `Generation failed (${res.status})`);
    }
    throw new Error(data.error || data.details || `Generation failed (${res.status})`);
  }
  return {
    ...data,
    folderId: data.folderId || targetFolderId,
    folderName: data.folderName || targetFolderName,
  };
}
